import { connectRouter } from 'connected-react-router';
import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import keyBy from 'lodash-es/keyBy';
import { combineReducers } from 'redux';
import { DEFAULT_RATE_CODE } from '../components/PaymentForm/PaymentFormHelper';
import { getNormalizedCode } from '../utilities/availabilityUtils';
import {
  BACKEND_PAYMENT_TYPES,
  GUARANTEE_TYPE,
  OFFER_CODE_TYPE,
  ROOM_FARE_FAMILY_RATE_TYPE,
  SUITE_TYPES,
  TAXES
} from '../utilities/constants';
import { FLEX_TRIP_PACKAGE_CODE } from '../utilities/paymentUtils';
import {
  getOfferCodeErrorDetails,
  getSuiteStandardFlexibleRates,
  isRateInFlexibleFamily
} from '../utilities/planUtils';
import { getTaxesAndFees, getTaxesAndFeesDynamic } from '../utilities/taxes';
import account from './componentStores/Account/account.reducer';
import { getUserIsLoggedIn } from './componentStores/Account/account.selectors';
import addApackage from './componentStores/addAPackage/addAPackage.reducer';
import { getAddedpackages } from './componentStores/addAPackage/addAPackage.selectors';
import birthdayParty from './componentStores/BirthdayParty/birthdayParty.reducer';
import cart from './componentStores/Cart/cart.reducer';
import { getHandoffCartId } from './componentStores/Cart/cart.selectors';
import currentLodge from './componentStores/CurrentLodge/currentLodge.reducer';
import dates from './componentStores/Dates/dates.reducer';
import { getCheckinDateSelection, getCheckoutDateSelection } from './componentStores/Dates/dates.selectors';
import dayPasses from './componentStores/DayPasses/dayPasses.reducer';
import flow from './componentStores/Flow/flow.reducer';
import globalModal from './componentStores/GlobalModal/globalModal.reducer';
import guests from './componentStores/Guests/guests.reducer';
import lodgeConfig from './componentStores/LodgeConfig/lodgeConfig.reducer';
import {
  getHiddenOfferPrefixesFromState,
  getIsHighestRatesEnabled,
  getTaxAndFeesLabels
} from './componentStores/LodgeConfig/lodgeConfig.selectors';
import manageReservationDetails from './componentStores/ManageReservation/manageReservation.reducer';
import {
  getCabanasAsPackages,
  getDueAtCheckinAmountWithCabanas,
  getPackageTotal,
  getPackageTotalWithCabanas,
  getRequestSuccess,
  getReservationData,
  getSummaryTotalWithCabana,
  getTaxesAmountWithCabanas
} from './componentStores/ManageReservation/manageReservation.selectors';
import offer from './componentStores/Offer/offer.reducer';
import { getDefaultOfferCode } from './componentStores/Offer/offer.selectors';
import packages from './componentStores/Packages/packages.reducer';
import {
  getPackages,
  getPackagesGrossTotal,
  getPackagesNetTotal,
  getPackagesTaxes
} from './componentStores/Packages/packages.selectors';
import plans from './componentStores/Plans/plans.reducer';
import { getIsChangedToFlex, getOfferCodeError } from './componentStores/Plans/plans.selectors';
import preCheckIn from './componentStores/PreCheckIn/preCheckIn.reducer';
import profile from './componentStores/Profile/profile.reducer';
import promoBanner from './componentStores/PromoBanner/promoBanner.reducer';
import reservations from './componentStores/Reservations/reservations.reducer';
import suite from './componentStores/Suite/suite.reducer';
import {
  getIsLoggedInAtSuiteSelection,
  getIsProperty,
  getParkingFee,
  getResortFee,
  getSelectedSuite,
  getSuiteGrossTotal,
  getSuiteNetTotal,
  getSuiteTaxes,
  getSustainabilityFee,
  getTaxesAndFeesDetailAvailability
} from './componentStores/Suite/suite.selectors';

// Follow A-Z order
const createRootReducer = history =>
  combineReducers({
    account,
    flow,
    addApackage,
    birthdayParty,
    cart,
    currentLodge,
    dates,
    dayPasses,
    entities: combineReducers({
      manageReservationDetails
    }),
    globalModal,
    guests,
    lodgeConfig,
    offer,
    plans,
    promoBanner,
    packages,
    profile,
    preCheckIn,
    router: connectRouter(history),
    reservations,
    suite
  });

export default createRootReducer;

/**
 * Get Suite Rate Total + Resort Fees to compare
 * @param {object} state Global state
 * @returns {number} Suite Rate total with fees
 */
export const getSuiteTotalWithFees = state => {
  const taxAndFeeLabels = getTaxAndFeesLabels(state.lodgeConfig);
  let totalFees = 0;
  if (!isEmpty(taxAndFeeLabels)) {
    const taxesAndFeesPrices = getTaxesAndFeesDetailAvailability(state.suite);
    const fees = getTaxesAndFeesDynamic(taxAndFeeLabels, taxesAndFeesPrices);
    totalFees = fees.reduce((acc, fee) => {
      if (fee.name !== TAXES) return acc + fee.amount;
      return acc;
    }, 0);
  } else {
    totalFees = getResortFee(state.suite) + getParkingFee(state.suite) + getSustainabilityFee(state.suite);
  }
  return getSuiteNetTotal(state.suite) + totalFees;
};

// Selectors
/**
 * Get summary total, this is suite + packages net totals.
 * @param {Object} state Global state
 * @return {number} Summary total
 */
export const getSummaryTotal = state => {
  return getPackagesNetTotal(state.packages) + getSuiteTotalWithFees(state);
};

/**
 * Get summary total, this is suite + packages gross totals.
 * @param {Object} state Global state
 * @return {number} Summary gross total
 */
export const getGrossSummaryTotal = state => {
  return getSuiteGrossTotal(state.suite) + getPackagesGrossTotal(state.packages);
};

/**
 * We can get the packages amount from different sources, we check in this order
 * 1. getPackagesNetTotal -- from this Packages Selectors
 * 2. getPackageTotal -- from Reservation Details Entity
 * 3. getAddedpackages -- And at least from getAddedPackages
 * @param {Object} state Store
 * @param {bool} fromReservation Flag to get the amounts from Reservation Detail entity
 */
export const getPackagesNetTotalFromSources = (state, fromReservation = false) => {
  let amount = 0;

  let packages = get(state, 'packages.selectedPackages', null);

  if (packages && !fromReservation) {
    amount = getPackagesNetTotal(state.packages);
  } else {
    // 1. Get the packages directly from reservation
    const reservationDetails = get(state, 'entities.manageReservationDetails', null);
    const addAPackageState = get(state, 'addApackage', null);
    if (reservationDetails) {
      amount = getPackageTotal(reservationDetails);
    }
    // 2. Get the packages from addAPackage state
    else if (addAPackageState) {
      const addedPackages = getAddedpackages(addAPackageState);
      const availabilityPackagesByCode = keyBy(getPackages(state.packages), 'packageCode');

      if (isEmpty(addedPackages)) return amount;

      Object.keys(addedPackages).reduce((packageTotalAcc, addedPackage) => {
        const packageAvailability = availabilityPackagesByCode[addedPackage];

        let packageTotal = packageTotalAcc;

        if (packageAvailability) {
          packageTotal = packageTotalAcc + packageAvailability.total * addedPackages[addedPackage].value;
        }

        return packageTotal;
      }, amount);
    }
  }

  return amount;
};

/**
 *
 * @param {object} state Redux State
 * @param {boolean} isResDetail Flag to check if the packages charge is at CMP page
 * @param {boolean} isDueToday Flag to check if the package charge is a due today at summary cost
 */
export const getPackagesNetTotalByPacksAtCheckout = (state, isResDetail = false, isDueToday = false) => {
  let guaranteeType = '';
  let offerType = '';
  let offerCode = '';

  // Get the offer type, place the amount if is ADV
  if (isResDetail) {
    guaranteeType = get(state, 'entities.manageReservationDetails.data.guaranteeType', '');

    offerCode = get(state, 'entities.manageReservationDetails.data.offerCode', '');

    // Add the due today on Group Offer Codes
    // We should add the amount and taxes just for the first one night
    offerType = get(state, 'suite.selectedSuite.offerType', '');
  } else {
    // Get guarantee and offer by availability call
    const { suiteDetailedAvailability, selectedSuite } = state.suite;
    const hasDetailedAvailability = suiteDetailedAvailability && suiteDetailedAvailability.length > 0;

    offerCode = get(state, 'offer.code', '');

    guaranteeType = hasDetailedAvailability ? get(suiteDetailedAvailability[0], 'guaranteeType', '') : '';

    offerType = get(selectedSuite, 'offerType', '');
  }

  // If we have the packages upfront disabled and
  // If the offer code doesn't require any advance payment
  // Like ADV or a Group Code then we don't send this calculation
  // Or if the offer code is AMTCERT we send a 0 for the due today
  if (
    isDueToday ||
    offerCode === DEFAULT_RATE_CODE ||
    (guaranteeType !== GUARANTEE_TYPE.advancePayment && offerType !== OFFER_CODE_TYPE.group)
  ) {
    // Flex package should be inside the Due Today.
    // So we get the selected packages and see if there is any flex package in there
    // If the condition don't fall here, getPackagesNetTotalFromSources take care about it
    const { packages } = state;
    const selectedPackages = get(packages, 'selectedPackages');
    if (selectedPackages) {
      const flexPackage = selectedPackages.find(pckg => pckg.packageCode === FLEX_TRIP_PACKAGE_CODE);
      return get(flexPackage, 'amount', 0);
    }
    return 0;
  }

  return getPackagesNetTotalFromSources(state, isResDetail);
};

/**
 * Get Due today amount.
 * @param {Object} state Suite store
 * @return {number} Due today amount.
 */
export const getDueTodayAmount = state => {
  const { suiteDetailedAvailability } = state.suite;

  let dueTodayAmount = 0;

  const hasDetailedAvailability = suiteDetailedAvailability && suiteDetailedAvailability.length > 0;

  // Add the due today only if guaranteeType is "ADV"
  // We should add the amount and taxes just for the first one night

  dueTodayAmount += hasDetailedAvailability ? suiteDetailedAvailability[0].advancedDeposit : 0;

  dueTodayAmount += getPackagesNetTotalByPacksAtCheckout(state, false, true);

  return dueTodayAmount;
};

/**
 * Get Due at checkin amount.
 * @param {Object} state Suite store
 * @return {number} Due at checkin amount.
 */
export const getDueAtCheckInAmount = state => {
  const summaryTotal = getSummaryTotal(state).toFixed(2);
  const dueTodayAmount = getDueTodayAmount(state).toFixed(2);
  return summaryTotal - dueTodayAmount;
};

// ─── Flex-Aware Selectors ───────────────────────────────────────────────────
// The selectedRate tier keeps these in step with getActiveFlexRate, which the booking payload
// reads: without it the page displayed one rate's money and booked another's.

export const getEffectiveLoggedInForFlex = state =>
  (getIsLoggedInAtSuiteSelection(state.suite) && getUserIsLoggedIn(state.account)) ||
  getSelectedSuite(state.suite)?.type?.toUpperCase() === ROOM_FARE_FAMILY_RATE_TYPE.voyager;

const getFlexAwareData = state => {
  const selectedSuite = getSelectedSuite(state.suite);
  const isEffectiveLoggedInForFlex = getEffectiveLoggedInForFlex(state);
  const isChangedToFlex = getIsChangedToFlex(state.plans);
  const isProperty = getIsProperty(state.suite);
  const isLoggedInNow = getUserIsLoggedIn(state.account);
  const isLoggedInAtSelection = getIsLoggedInAtSuiteSelection(state.suite);
  const wasInitiallyFlexRate = isRateInFlexibleFamily(selectedSuite, selectedSuite?.rateCode);
  const { flexibleRate, standardRate } = getSuiteStandardFlexibleRates(selectedSuite, isEffectiveLoggedInForFlex);
  const canUseFlexData = !isProperty && !!flexibleRate && (isChangedToFlex || wasInitiallyFlexRate);
  const canUseStandardRateOverride =
    !wasInitiallyFlexRate && !isProperty && (!isLoggedInNow || !isLoggedInAtSelection) && !!standardRate;
  const selectedRate = isProperty
    ? null
    : (selectedSuite?.rateFamilies ?? [])
        .flatMap(family => family.rates ?? [])
        .find(rate => rate?.rateCode === selectedSuite?.rateCode) ?? null;
  const activeRate = canUseFlexData ? flexibleRate : canUseStandardRateOverride ? standardRate : selectedRate;
  return { canUseFlexData, flexibleRate, canUseStandardRateOverride, standardRate, activeRate };
};

export const getFlexAwareSuiteTotal = state => {
  const { activeRate } = getFlexAwareData(state);
  if (typeof activeRate?.totalRate === 'number') return activeRate.totalRate;
  return getSuiteGrossTotal(state.suite);
};

export const getFlexAwareTaxesAndFees = state => {
  const { activeRate } = getFlexAwareData(state);
  const taxAndFeeLabels = getTaxAndFeesLabels(state.lodgeConfig);
  const packagesTaxes = getPackagesTaxes(state.packages);
  const taxesAndFeesPrices = activeRate ? activeRate.taxesAndFees : getTaxesAndFeesDetailAvailability(state.suite);

  if (!isEmpty(taxAndFeeLabels) && !isEmpty(taxesAndFeesPrices)) {
    return getTaxesAndFeesDynamic(taxAndFeeLabels, taxesAndFeesPrices, packagesTaxes);
  }

  const activeSuiteTaxes = activeRate ? activeRate.taxesAndFees?.TAX ?? 0 : getSuiteTaxes(state.suite);
  const activeResortFee = activeRate ? activeRate.resortFee ?? 0 : getResortFee(state.suite);
  const activeSustainabilityFee = activeRate ? activeRate.sustainabilityFee ?? 0 : getSustainabilityFee(state.suite);
  const activeParkingFee = activeRate ? activeRate.parkingFee ?? 0 : getParkingFee(state.suite);
  return getTaxesAndFees(activeSustainabilityFee, packagesTaxes + activeSuiteTaxes, activeResortFee, activeParkingFee);
};

const getFlexSuiteTotalWithFees = (state, rate) => {
  const taxAndFeeLabels = getTaxAndFeesLabels(state.lodgeConfig);
  const activeSuiteTaxes = rate.taxesAndFees?.TAX ?? 0;
  const activeResortFee = rate.resortFee ?? 0;
  const activeSustainabilityFee = rate.sustainabilityFee ?? 0;
  const activeParkingFee = rate.parkingFee ?? 0;

  let totalNonTaxFees;
  if (!isEmpty(taxAndFeeLabels) && !isEmpty(rate.taxesAndFees)) {
    const feesList = getTaxesAndFeesDynamic(taxAndFeeLabels, rate.taxesAndFees);
    totalNonTaxFees = feesList.reduce((acc, fee) => (fee.name !== TAXES ? acc + fee.amount : acc), 0);
  } else {
    totalNonTaxFees = activeResortFee + activeSustainabilityFee + activeParkingFee;
  }

  return rate.totalRate + activeSuiteTaxes + totalNonTaxFees;
};

export const getIsHighestRatesEnabledForCurrentFlow = state =>
  getIsHighestRatesEnabled(state.lodgeConfig) && !getHandoffCartId(state.cart);

export const getFlexAwareSummaryTotal = state => {
  const { activeRate } = getFlexAwareData(state);
  if (activeRate) return getFlexSuiteTotalWithFees(state, activeRate) + getPackagesNetTotal(state.packages);
  return getSummaryTotal(state);
};

export const getFlexAwareDueTodayAmount = state => {
  const { activeRate } = getFlexAwareData(state);
  const dueTodayAmount = getDueTodayAmount(state);
  if (!activeRate) return dueTodayAmount;
  const standardAdvancedDeposit = state.suite.suiteDetailedAvailability?.[0]?.advancedDeposit ?? 0;
  const packagesDepositPortion = dueTodayAmount - standardAdvancedDeposit;
  return (activeRate.advancedDeposit ?? 0) + packagesDepositPortion;
};

export const getFlexAwareDueAtCheckinAmount = state => {
  const { activeRate } = getFlexAwareData(state);
  if (!activeRate) return getDueAtCheckInAmount(state);
  return getFlexAwareSummaryTotal(state).toFixed(2) - getFlexAwareDueTodayAmount(state).toFixed(2);
};

export const getActiveFlexRate = state => {
  if (getIsProperty(state.suite) || !getSelectedSuite(state.suite)) return null;

  return getFlexAwareData(state).activeRate;
};

export const getIsFlexRateOffered = state => !getIsProperty(state.suite) && !!getFlexAwareData(state).flexibleRate;

export const getFlexRatePriceDelta = state => {
  const { canUseFlexData, flexibleRate, standardRate, activeRate } = getFlexAwareData(state);
  if (!flexibleRate) return 0;
  const baseline = canUseFlexData ? standardRate : activeRate;
  const standardSuiteTotal = baseline ? getFlexSuiteTotalWithFees(state, baseline) : getSuiteTotalWithFees(state);
  return getFlexSuiteTotalWithFees(state, flexibleRate) - standardSuiteTotal;
};

// ────────────────────────────────────────────────────────────────────────────

/**
 * Get the sum of packages taxes + suite taxes.
 * @param {Object} state Global state
 * @return {number} Taxes from suite and packages
 */
export const getSummaryTaxes = state => {
  return getSuiteTaxes(state.suite) + getPackagesTaxes(state.packages);
};

/**
 * Shapes the data related to the additional packages added for add a package
 * store. The package for this objects is merged from availability packages
 * data and the actual interactions from the user saved in addApackage strore.
 * @param {Object} state Global state
 * @return {array} List of objects containing additional packages data.
 */
export const getAditionalPackagesFromAddApackage = state => {
  const availabilityPackages = keyBy(getPackages(state.packages), 'packageCode');

  const addedPackages = getAddedpackages(state.addApackage);

  // console.log('US#29565 - availabilityPackages', availabilityPackages);
  // console.log('US#29565 - addedPackages', addedPackages);

  const additionalPackages = Object.keys(addedPackages).map(packageId => {
    const packageFromAvailability = availabilityPackages[packageId];

    let additionalPackage = null;

    if (packageFromAvailability) {
      additionalPackage = {
        id: packageFromAvailability.packageCode,
        title: packageFromAvailability.title,
        packageAmount: packageFromAvailability.amount * addedPackages[packageId].value,
        quantityAdded: addedPackages[packageId].value,
        options: addedPackages[packageId].options,
        additionalTitle: packageFromAvailability.title,
        packageType: packageFromAvailability.packageType[0],
        chargeType: packageFromAvailability.chargeType
      };
    }

    return additionalPackage;
  });

  return additionalPackages.filter(Boolean);
};

/**
 * Get total package price from reservation detail taking into account if there
 * were added packages.
 * @param {Object} state GLobal state.
 * @return {number} Package total.
 */
export const getPackageTotalWithAdditionalPackages = state => {
  const reservationDetails = state.entities.manageReservationDetails;
  const addedPackages = getAddedpackages(state.addApackage);
  const packageTotal = getPackageTotalWithCabanas(reservationDetails);

  const availabilityPackagesByCode = keyBy(getPackages(state.packages), 'packageCode');

  if (isEmpty(addedPackages)) return packageTotal;
  const addedPackagesTotal = Object.keys(addedPackages).reduce((packageTotalAcc, addedPackage) => {
    const packageAvailability = availabilityPackagesByCode[addedPackage];

    if (!packageAvailability) return packageTotalAcc;

    return packageTotalAcc + packageAvailability.amount * addedPackages[addedPackage].value;
  }, packageTotal);

  return addedPackagesTotal;
};

/**
 * Get total taxes amount from reservation detail taking into account if there
 * were added packages.
 * @param {Object} state GLobal state.
 * @return {number} Package total.
 */
export const getTaxesAmountWithAdditionalPackages = state => {
  const reservationDetails = state.entities.manageReservationDetails;
  const taxesAmount = getTaxesAmountWithCabanas(reservationDetails);
  const addedPackages = getAddedpackages(state.addApackage);
  const availabilityPackagesByCode = keyBy(getPackages(state.packages), 'packageCode');

  if (isEmpty(addedPackages)) return taxesAmount;

  return Object.keys(addedPackages).reduce((packageTotalAcc, addedPackage) => {
    const packageAvailability = availabilityPackagesByCode[addedPackage];

    if (!packageAvailability) return packageTotalAcc;

    return packageTotalAcc + packageAvailability.tax * addedPackages[addedPackage].value;
  }, taxesAmount);
};

/**
 * Get summary total from reservation detail taking into account if there
 * were added packages.
 * @param {Object} state GLobal state.
 * @return {number} Package total.
 */
export const getSummaryTotalWithAdditionalPackages = state => {
  const reservationDetails = state.entities.manageReservationDetails;
  const summaryTotal = getSummaryTotalWithCabana(reservationDetails);
  const addedPackages = getAddedpackages(state.addApackage);
  const availabilityPackagesByCode = keyBy(getPackages(state.packages), 'packageCode');

  if (isEmpty(addedPackages)) return summaryTotal;

  const packagesTotal = Object.keys(addedPackages).reduce((packageTotalAcc, addedPackage) => {
    const packageAvailability = availabilityPackagesByCode[addedPackage];

    let packageTotal = packageTotalAcc;

    if (packageAvailability) {
      packageTotal = packageTotalAcc + packageAvailability.total * addedPackages[addedPackage].value;
    }

    return packageTotal;
  }, summaryTotal);

  return packagesTotal;
};

/**
 * @Function
 * Gets the amount due at checkin with affirm
 * @param {Object} state
 * @param {Number} defaultAmount
 * @returns {Object} with the keys: {isAlreadyPaidWithAffirm, affirmAmount}.
 * The Flag isAlreadyPaidWithAffirm means the current reservation was paid with affirm
 * Amount is the amount paid with affirm, this value can be overwritten to fullfill some scenarios
 * such the dueAtCheckIn calculation
 */
export const getAffirmPaymentDueAtCheckIn = (state, defaultAmount = null) => {
  const reservationData = get(state.entities, 'manageReservationDetails.data', {});

  // If the reservation data is not empty
  if (!isEmpty(reservationData)) {
    const affirmAmount = defaultAmount ? defaultAmount : 0;

    const isAlreadyPaidWithAffirm = get(reservationData, 'isAffirm');

    return { isAlreadyPaidWithAffirm, affirmAmount };
  }

  return { isAlreadyPaidWithAffirm: false, affirmAmount: defaultAmount ? defaultAmount : 0 };
};

/**
 * Get due at check in amount from reservation detail taking into account if
 * there were added packages.
 * @param {Object} state GLobal state.
 * @return {number} Package total.
 */
export const getDueAtCheckinAmountWithAdditionalPackages = state => {
  // If is paid with affirm we return 0
  const { isAlreadyPaidWithAffirm, affirmAmount } = getAffirmPaymentDueAtCheckIn(state, 0);
  if (isAlreadyPaidWithAffirm) {
    return affirmAmount;
  }
  const reservationDetails = state.entities.manageReservationDetails;
  // If is not paid with affirm then the codes flows as normal
  const dueAtCheckinAmount = getDueAtCheckinAmountWithCabanas(reservationDetails);

  const addedPackages = getAddedpackages(state.addApackage);

  // Additional Packages
  const additionalPackagesTotal = Object.keys(addedPackages).reduce((acc, packageKey) => {
    const { value } = addedPackages[packageKey] || {};
    const selectedPackage = state.packages.packageAvailabilityList.find(item => item.packageCode === packageKey);

    const packageMultiplier = value ? value : 0;

    if (packageMultiplier && selectedPackage && selectedPackage.total) {
      return acc + selectedPackage.total * packageMultiplier;
    }

    return acc;
  }, 0);

  return dueAtCheckinAmount + additionalPackagesTotal;
};

/**
 * Gets a package with amount for the package section in the cost summary.
 * @param {string} packageCode for differentiate it in the package section for the cost summary.
 * @param {string} packageTitle which will appear in the package section for the cost summary.
 * @param {Package Amount} amount package amount.
 * @param {number} quantity for the package.
 * @returns Package for the package section.
 */
const buildPackageWithAmount = (
  packageCode,
  packageTitle,
  amount,
  quantity,
  packageType = '',
  packageCharge = '',
  packageAllowance = 0,
  diningCredits
) => {
  return {
    packageCode: packageCode,
    packageTitle: packageTitle,
    packageAmount: amount,
    quantity: quantity,
    packageType,
    packageCharge,
    packageAllowance: packageAllowance,
    diningCredits
  };
};

/**
 * Get packages from reservation details entity and package amount.
 * @param {Object} state Global state.
 * @return {Object} Object shaped to be used.
 */
export const getPackagesWithAmount = state => {
  const reservationDetails = state.entities.manageReservationDetails;
  const data = getReservationData(reservationDetails);
  const requestSuccess = getRequestSuccess(reservationDetails);

  //if we get a false on the requestSuccess and we don't have existing data we end the run
  if (!requestSuccess && !data) return;
  //if we are done loading we check if we have data
  if (!data || Object.keys(data).length === 0) return;

  const reservationPackages = data.addOns;
  if (!reservationPackages) return;

  const formattedPackages = reservationPackages.map(pck => {
    return buildPackageWithAmount(
      pck.code,
      pck.content.title,
      pck.totalPrice,
      pck.quantity,
      pck.type,
      pck.packageCharge,
      pck.content.diningCredits
    );
  });

  return formattedPackages;
};

export const getOfferCodeErrorObj = state => {
  const offerCode = getDefaultOfferCode(state.offer);
  const { errorCode } = getOfferCodeError(state.plans) || {};
  const checkIn = getCheckinDateSelection(state.dates);
  const checkOut = getCheckoutDateSelection(state.dates);
  const hiddenOfferPrefixes = getHiddenOfferPrefixesFromState(state.lodgeConfig);

  if (!checkIn || !checkOut) return;

  return getOfferCodeErrorDetails({
    offerCode,
    offerCodeError: errorCode,
    checkIn,
    checkOut,
    hiddenOfferPrefixes
  });
};

/**
 * Return and offer code error message with dynamic number of days if applies.
 * @param {*} state Global state.
 * @return {string|undefined} Offer code error message or undefined if valid.
 */
export const getOfferCodeErrorMessage = state => {
  const offerCodeErrorDetail = getOfferCodeErrorObj(state);

  return offerCodeErrorDetail && offerCodeErrorDetail.message;
};

/**
 * @Function
 * Gets the amount payed with affirm, if a default amount is setted, then that amount is returned
 * amongside with the isAlreadyPaidWithAffirm flag
 * @param {Object} state
 * @returns {Object} with the keys: {isAlreadyPaidWithAffirm, affirmAmount}.
 * The Flag isAlreadyPaidWithAffirm means the current reservation was paid with affirm
 * Amount is the amount paid with affirm, this value can be overwritten to fullfill some scenarios
 * such the dueAtCheckIn calculation
 */
export const getAffirmPayment = state => {
  const reservationData = get(state.entities, 'manageReservationDetails.data', {});

  // If the reservation data is not empty
  if (!isEmpty(reservationData)) {
    const affirmAmount = get(reservationData, 'rateInfo.total');

    const paymentype = get(reservationData, 'paymentType');

    const isAlreadyPaidWithAffirm = paymentype === BACKEND_PAYMENT_TYPES.Affirm;

    return { isAlreadyPaidWithAffirm, affirmAmount };
  }

  return { isAlreadyPaidWithAffirm: false, affirmAmount: 0 };
};

export const getValidOfferCode = state => {
  const offerCode = getDefaultOfferCode(state.offer);
  const offerCodeError = getOfferCodeError(state.plans);

  if (offerCode && (!offerCodeError || (offerCodeError && !offerCodeError.errorCode))) {
    return getNormalizedCode(offerCode);
  }

  return '';
};

export const getValidRateCode = (state, suite) => {
  const defaultRateCodeSuites = 'BBAR';
  const defaultRateCodeCondos = 'BARC';

  const offerCode = getValidOfferCode(state);
  const isCondo = suite.suiteType === SUITE_TYPES.condo;
  const defaultRateCode = isCondo ? defaultRateCodeCondos : defaultRateCodeSuites;

  if (!offerCode) {
    return defaultRateCode;
  }

  return suite.rateCode ?? offerCode;
};

/**
 * Get List Packages from Reservation Details
 * @param {Object} state Redux state
 * @return {Array.<Object>} List of packages from Reservation Details
 */
export const getReservationPackages = state => {
  const reservationPackages = getReservationPackages(state);
  return reservationPackages;
};

/**
 * Get current booking engine rates to send to backend
 * @param {object} state Redux state
 * @param {bool} isBookingFlow Check if isBookingFlow (this is the reservation normal flow) or daypasses
 * @returns Array.object With the current booking engine rates
 */
export const getSuiteRates = (state, isBookingFlow) => {
  let detailedAvailability = [];

  if (isBookingFlow) {
    detailedAvailability = get(state, 'suite.suiteDetailedAvailability', []);
  } else {
    const { dayPasses } = state;
    const dayPassItems = get(dayPasses, 'item', []);
    const dayPassCode = get(dayPasses, 'selectedItemCode', '');

    for (var key in dayPassItems) {
      var item = dayPassItems[key];
      if (item.itemCode === dayPassCode) {
        detailedAvailability = [
          {
            date: item.date,
            suiteCode: item.itemCode,
            totalNightlyRate: item.roomRateTotal,
            taxesAndFees: item.taxesAndFees
          }
        ];
      }
    }
  }

  return detailedAvailability;
};

export const showLoyalty = state => {
  const isLoyaltyEnabled = state.lodgeConfig.isLoyaltyEnabled;
  const isPrimerState = state.lodgeConfig.isInPrimerState;
  const isUserSignedIn = state.account.userIsLoggedIn;
  const loyaltyOptIn = state.account.currentLoggedInUser?.loyaltyOptIn || false;

  if (isLoyaltyEnabled === false) return false; // no Loyalty if in 'Dark' state

  // not in 'dark' and not in 'LLP' - BkgEng
  if (isPrimerState === true) {
    // BkgEng with Loyalty in 'Primer' state
    if (isUserSignedIn === true) {
      // BkgEng with Loyalty in 'Primer' state + user is logged in
      if (loyaltyOptIn === true) {
        // BkgEng with Loyalty in 'Primer' state + user is logged in + user is Opted in Loyalty program
        return true; //
      } // BkgEng with Loyalty in 'Primer' state + user is logged in + user is NOT Opted in Loyalty program
      else {
        return false;
      }
    } // BkgEng with Loyalty in 'Primer' state + guest user
    else {
      return false;
    }
  } // BkgEng with Loyalty in 'Public' state
  else {
    return true;
  }
};

/**
 * Get the total amount of the packages
 * @param {*} state
 * @returns
 */
export const packagesWithAmountFromReservationData = state => {
  const reservationDetails = state.entities.manageReservationDetails;
  const data = getReservationData(reservationDetails);
  const requestSuccess = getRequestSuccess(reservationDetails);

  //if we get a false on the requestSuccess and we don't have existing data we end the run
  if (!requestSuccess && !data) return;
  //if we are done loading we check if we have data
  if (!data || Object.keys(data).length === 0) return;

  const reservationPackages = data?.addOns ?? data?.packages ?? [];
  const cabanas = getCabanasAsPackages(reservationDetails) ?? [];

  return [...reservationPackages, ...cabanas].map(pkg => {
    return {
      amount: pkg.totalPrice ?? pkg.packageAmount ?? 0,
      packageAllowance: pkg.allowance ?? pkg.content?.diningCredits ?? 0,
      packageCharge: pkg.chargeType ?? pkg.content?.chargeType ?? '',
      packageCode: pkg.code ?? pkg.packageCode ?? '',
      packageName: pkg.content?.title ?? pkg.packageTitle ?? pkg.shortDescription ?? pkg.description ?? '',
      packageType: pkg.type ?? pkg.packageType ?? '',
      quantity: pkg.quantity ?? 0,
      creditsTitle: pkg.content?.creditsTitle ?? null
    };
  });
};
